import { Grid, CardHeader, CardContent, Box, Select, FormHelperText, MenuItem, TextField, CircularProgress, Avatar, Button } from "@mui/material";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Form, FormikProvider, useFormik } from "formik";
import { IMAGE_BASE, REMOVEHTML, REMOVENBSP } from "../../utils/constants";
import LoadingButton from "@mui/lab/LoadingButton";
import CardLayout from "../../layouts/CardLayout";
import { toast } from "react-toastify";
import errorHandler from "../../utils/errorHandler";
import { AutionBidsServices } from "../../services/AllServices";
import EdiorJodit from "../../components/editor/editorJodit";
import { AuctionTaskSchema } from "../../utils/Schema";

function AddEditCategory() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [imageLoading, setImageLoading] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [cmsData, setCmsData] = useState("");
    const fileInputRef = useRef(null);
    const [data, setData] = useState();
    const fetchTaskData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await AutionBidsServices.getData();
            const resData = response?.data?.data;
            setImagePreview(IMAGE_BASE + resData?.thumbnail);
            setData(resData);
            setCmsData(resData?.notice)
            setLoading(false);
        } catch (error) {
            errorHandler(error);
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTaskData();
    }, [fetchTaskData]);

    const formik = useFormik({
        enableReinitialize: true,
        validationSchema: AuctionTaskSchema,
        initialValues: {
            title: data?.title || "",
            purchaseRange: data?.purchaseRange || "",
            freeBidsGranted: data?.freeBidsGranted || "",
            notice: data?.notice || "",
            image: data?.thumbnail || "",
            status: data?.status ? true : false,
        },
        onSubmit: async (values) => {
            setLoading(true);
            try {
                const formData = new FormData();
                formData.append("title", values.title);
                formData.append("purchaseRange", values.purchaseRange);
                formData.append("freeBidsGranted", values.freeBidsGranted);
                formData.append("notice", values.notice);
                formData.append("status", values.status);
                formData.append("imagePath", "task");
                if (selectedImage && selectedImage !== data?.image) {
                    formData.append("image", selectedImage);
                }
                const response = await AutionBidsServices.update(formData);

                toast.success(response?.data?.message, { toastId: "success" });
                // navigate(-1);
            } catch (error) {
                console.error(error);
                errorHandler(error);
            } finally {
                setLoading(false);
            }
        },
    });

    const handleImageChange = (event) => {
        const file = event.currentTarget.files[0];
        const validTypes = ["image/jpeg", "image/png", "image/gif"];

        if (selectedImage && selectedImage.name === file?.name) {
            toast.error("You cannot upload the same image again.");
            fileInputRef.current.value = "";
            return;
        }

        if (file && validTypes.includes(file.type)) {
            setImageLoading(true);
            setSelectedImage(file);
            setImagePreview(URL.createObjectURL(file));
            formik.setFieldValue("image", file);
            setTimeout(() => {
                setImageLoading(false);
            }, 2000);
        } else {
            toast.error("Please upload a valid image (JPEG, PNG, or GIF).");
            fileInputRef.current.value = "";
        }
    };

    return (
        <Box>
            <CardLayout>
                <CardHeader title={`Pay in Auction to Earn Bids`} />
                <CardContent>
                    <FormikProvider value={formik}>
                        <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
                            <Grid container spacing={3}>

                                <Grid item xs={12} md={12}>
                                    <input
                                        accept="image/*"
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleImageChange}
                                        style={{ display: "none" }}
                                    />

                                    <Box
                                        mt={2}
                                        onClick={() => fileInputRef.current.click()}
                                        className="upload-box"
                                    >
                                        {imageLoading ? (
                                            <CircularProgress size={24} />
                                        ) : (
                                            <Avatar
                                                src={imagePreview || "/path/to/default-avatar.jpg"}
                                                alt="preview"
                                                className="avatar"
                                            />
                                        )}
                                    </Box>
                                    {formik.touched.image && formik.errors.image ? (
                                        <div className="error-msg error-category">
                                            {formik.errors.image}
                                        </div>
                                    ) : null}

                                    <Box mt={2} display="flex" justifyContent="center">
                                        <Button
                                            size="large"
                                            type="button"
                                            variant="contained"
                                            onClick={() => fileInputRef.current.click()}
                                        >
                                            Upload Image
                                        </Button>
                                    </Box>
                                </Grid>

                                <Grid item xs={6} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Title"
                                        value={formik.values.title}
                                        onChange={formik.handleChange}
                                        name="title"
                                        error={Boolean(formik.touched.title && formik.errors.title)}
                                        helperText={formik.touched.title && formik.errors.title}
                                        inputProps={{ maxLength: 100 }}
                                    />
                                </Grid>

                                <Grid item xs={6} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Purchase Range"
                                        value={formik.values.purchaseRange}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            // Allow only numbers with up to 2 decimal places
                                            if (/^\d*\.?\d{0,2}$/.test(value)) {
                                                formik.setFieldValue("purchaseRange", value);
                                            }
                                        }}
                                        name="purchaseRange"
                                        error={Boolean(formik.touched.purchaseRange && formik.errors.purchaseRange)}
                                        helperText={formik.touched.purchaseRange && formik.errors.purchaseRange}
                                        inputProps={{ inputMode: "decimal", pattern: "[0-9]+(.[0-9]{0,2})?" , maxLength: 4 }}
                                    />


                                </Grid>

                                <Grid item xs={12} md={12}>
                                    <TextField
                                        fullWidth
                                        label="Free Bids"
                                        value={formik.values.freeBidsGranted}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            // Allow only numbers with up to 2 decimal places
                                            if (/^\d*\.?\d{0,2}$/.test(value)) {
                                                formik.setFieldValue("freeBidsGranted", value);
                                            }
                                        }}
                                        name="freeBidsGranted"
                                        error={Boolean(formik.touched.freeBidsGranted && formik.errors.freeBidsGranted)}
                                        helperText={formik.touched.freeBidsGranted && formik.errors.freeBidsGranted}
                                        inputProps={{ inputMode: "decimal", pattern: "[0-9]+(.[0-9]{0,2})?" }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={12}>
                                    <EdiorJodit
                                        data={formik?.values?.notice}
                                        placeholder="Terms and conditions"
                                        onChange={(html) => {
                                            formik.setFieldTouched("notice", true);
                                            const withoutHtml = html.replace(REMOVEHTML, "").replace(REMOVENBSP, "").trim();
                                            formik.setFieldValue("notice", withoutHtml ? html : "");
                                            setCmsData(html);
                                        }}
                                    />

                                    {formik.touched.notice && formik.errors.notice && (
                                        <p style={{ color: "red", fontSize: "14px" }}>
                                            {formik.errors.notice}
                                        </p>
                                    )}
                                </Grid>

                                <Grid item xs={12} md={12}>
                                    <Select
                                        labelId="status-label"
                                        id="status"
                                        value={formik.values.status}
                                        onChange={(e) =>
                                            formik.setFieldValue("status", e.target.value)
                                        }
                                        fullWidth
                                        error={Boolean(
                                            formik.touched.status && formik.errors.status
                                        )}
                                        displayEmpty
                                        defaultValue=""
                                    >
                                        <MenuItem value="">Select Status</MenuItem>
                                        <MenuItem value='true'>Active</MenuItem>
                                        <MenuItem value='false'>Inactive</MenuItem>
                                    </Select>
                                    {formik.touched.status && formik.errors.status && (
                                        <FormHelperText error>
                                            {formik.errors.status}
                                        </FormHelperText>
                                    )}
                                </Grid>

                                <Grid item xs={12}>
                                    <Box mt={3} width="100%">
                                        <LoadingButton
                                            fullWidth
                                            size="large"
                                            type="submit"
                                            variant="contained"
                                            loading={loading}
                                        >
                                            Update Auction
                                        </LoadingButton>
                                    </Box>
                                </Grid>

                            </Grid>
                        </Form>
                    </FormikProvider>
                </CardContent>
            </CardLayout>
        </Box>
    );
}

export default AddEditCategory;